export default function createAccessibleSlickGrid($) {
	function escapeHtml(value) {
		return String(value)
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#039;");
	}

	function editRoleAriaLabel(email) {
		return (
			"Edit role for " +
			escapeHtml(email)
		);
	}

	function removeUserAriaLabel(email) {
		return (
			"Remove user " +
			escapeHtml(email)
		);
	}

	function buildEditButtonHtml(email) {
		return (
			'<button' +
			' type="button"' +
			' class="fff-pencil"' +
			' data-grid-action="edit-role"' +
			' tabindex="-1"' +
			' aria-label="' +
			editRoleAriaLabel(email) +
			'"' +
			' style="' +
			'position:absolute;' +
			' top:3px;' +
			' right:3px;' +
			' border:none;' +
			'">' +
			"</button>"
		);
	}

	function isOptedIn($container) {
		return (
			$container.length > 0 &&
			$container.attr(
				"data-accessible-slick-grid"
			) === "true"
		);
	}

	function isPlainKey(event) {
		return (
			!event.altKey &&
			!event.ctrlKey &&
			!event.metaKey
		);
	}

	function isTabKey(event) {
		return (
			event.key === "Tab" ||
			event.keyCode === 9
		);
	}

	/*
	 * Which way the user was tabbing when focus last moved. A grid is a single
	 * DOM tab stop whose first virtual stop depends on the direction: Tab from
	 * the control before it starts on the header row, Shift+Tab from the control
	 * after it starts in the body, so the row of headers is passed exactly once
	 * in each direction. focusin cannot tell the two apart on its own, and a
	 * mouse click or a scripted focus() must not be mistaken for either, hence
	 * the short window: only a focus that follows a Tab keystroke counts.
	 */
	let lastTabKeyAt = 0;
	let lastTabWasBackward = false;
	const TAB_FOCUS_WINDOW_MS = 150;

	$(document).off(
		"keydown.accessibleSlickGridTabDirection"
	).on(
		"keydown.accessibleSlickGridTabDirection",
		function (event) {
			if (isTabKey(event)) {
				lastTabKeyAt = Date.now();
				lastTabWasBackward = !!event.shiftKey;
			}
		}
	);

	/*
	 * true  - focus arrived on a Tab from the control before the grid
	 * false - focus arrived on a Shift+Tab from the control after it
	 * null  - not a tab move (click, scripted focus, or too long ago)
	 */
	function tabDirectionIntoGrid() {
		if (
			Date.now() - lastTabKeyAt >
			TAB_FOCUS_WINDOW_MS
		) {
			return null;
		}

		return !lastTabWasBackward;
	}

	function consumeEvent(event) {
		event.preventDefault();
		event.stopPropagation();

		if (
			event.stopImmediatePropagation
		) {
			event.stopImmediatePropagation();
		}
	}

	function getColumnIndexFromCell(
		cellNode
	) {
		let match =
			/\bl(\d+)\b/.exec(
				cellNode.className
			);

		return match
			? parseInt(match[1], 10)
			: -1;
	}

	function getRowIndexFromCell(
		cellNode
	) {
		let rowValue =
			$(cellNode)
				.closest(".slick-row")
				.attr("row");

		let rowIndex =
			parseInt(rowValue, 10);

		return isNaN(rowIndex)
			? -1
			: rowIndex;
	}

	function focusContainer(container) {
		try {
			container.focus({
				preventScroll: true
			});
		} catch (error) {
			container.focus();
		}
	}

	function getGridFocusTarget(
		$container
	) {
		let $viewport =
			$container.find(
				".slick-viewport"
			).first();

		return $viewport.length
			? $viewport[0]
			: $container[0];
	}

	function getHeaderCells(
		$container
	) {
		return $container
			.find(
				".slick-header-columns" +
				" > .slick-header-column"
			)
			.toArray();
	}

	function focusHeaderCell(
		$container,
		grid,
		columnIndex
	) {
		let headerCells =
			getHeaderCells(
				$container
			);

		if (!headerCells.length) {
			return -1;
		}

		let clampedIndex =
			Math.max(
				0,
				Math.min(
					columnIndex,
					headerCells.length - 1
				)
			);

		let headerCell =
			headerCells[clampedIndex];

		/*
		 * Keep SlickGrid's own active-cell bookkeeping pointed at this
		 * column so that a later async refresh (e.g. after a remote
		 * sort) sees a non-null, matching active cell and never falls
		 * back to forcing column 0 via ensureActiveCell().
		 */
		if (grid.getCellNode(0, clampedIndex)) {
			grid.setActiveCell(
				0,
				clampedIndex
			);

			/*
			 * setActiveCell moves real DOM focus to SlickGrid's own
			 * canvas element. Return it to the grid root so that later
			 * checks against document.activeElement (e.g. in refresh())
			 * keep working and the header stays the real focus target.
			 */
			focusContainer(
				getGridFocusTarget(
					$container
				)
			);
		}

		$container
			.find(
				".slick-cell.accessible-active-cell," +
				" .slick-cell.active"
			)
			.removeClass(
				"accessible-active-cell active"
			);

		$container
			.find(
				".slick-header-column" +
				".accessible-active-header"
			)
			.removeClass(
				"accessible-active-header"
			);

		$(headerCell).addClass(
			"accessible-active-header"
		);

		$container.addClass(
			"accessible-grid-focused"
		);

		$(getGridFocusTarget(
			$container
		)).attr(
			"aria-activedescendant",
			headerCell.id
		);

		return clampedIndex;
	}

	function bindSelectEditorKeyboard(
		input,
		args,
		gridContainer
	) {
		let $input =
			input && input.jquery
				? input
				: $(input);

		let $gridContainer =
			gridContainer && gridContainer.jquery
				? gridContainer
				: $(gridContainer);

		function stopPropagation(event) {
			event.stopPropagation();

			if (
				event.stopImmediatePropagation
			) {
				event.stopImmediatePropagation();
			}
		}

		function restoreGridFocus() {
			window.setTimeout(function () {
				if ($gridContainer.length) {
					focusContainer(
						getGridFocusTarget(
							$gridContainer
						)
					);
				}
			}, 0);
		}

		function commitAndNavigate(
			methodName
		) {
			let grid = args.grid;
			let editorLock =
				grid.getEditorLock();

			let previousCell =
				grid.getActiveCell();

			if (
				!editorLock.commitCurrentEdit()
			) {
				return;
			}

			if (
				previousCell &&
				typeof grid[methodName] ===
				"function"
			) {
				grid[methodName]();
			}

			window.setTimeout(function () {
				let activeCell =
					grid.getActiveCell();

				if (!activeCell) {
					restoreGridFocus();
					return;
				}

				let cellChanged =
					!previousCell ||
					activeCell.row !==
					previousCell.row ||
					activeCell.cell !==
					previousCell.cell;

				/*
				 * Navigation cannot move past the grid boundary.
				 * Reopen the editor in the same cell.
				 */
				if (
					!cellChanged &&
					typeof grid.gotoCell ===
					"function"
				) {
					grid.gotoCell(
						activeCell.row,
						activeCell.cell,
						true
					);

					window.setTimeout(function () {
						syncActiveDescendant(
							$gridContainer,
							grid
						);
					}, 0);

					return;
				}

				/*
				 * Navigation moved to another cell.
				 * Return focus to the grid root and leave edit mode.
				 */
				restoreGridFocus();

				window.setTimeout(function () {
					syncActiveDescendant(
						$gridContainer,
						grid
					);
				}, 0);
			}, 0);
		}

		/*
		* Remove any previously registered editor handler.
		*/
		$input.off(
			".accessibleSlickGridEditor"
		);

		$input.on(
			"keydown.accessibleSlickGridEditor",
			function (event) {
				let key =
					event.key ||
					event.keyCode;

				let isEnter =
					key === "Enter" ||
					key === 13;

				let isSpace =
					key === " " ||
					key === "Spacebar" ||
					key === 32;

				let isEscape =
					key === "Escape" ||
					key === "Esc" ||
					key === 27;

				let isArrowLeft =
					key === "ArrowLeft" ||
					key === 37;

				let isArrowRight =
					key === "ArrowRight" ||
					key === 39;

				let isArrowUp =
					key === "ArrowUp" ||
					key === 38;

				let isArrowDown =
					key === "ArrowDown" ||
					key === 40;

				/*
				 * Enter and Space retain the native select behavior.
				 * Prevent the event from reaching SlickGrid.
				*/
				if (
					isEnter ||
					isSpace
				) {
					stopPropagation(event);
					return;
				}

				/*
				* Escape cancels the current edit
				* and returns focus to the grid.
				*/
				if (isEscape) {
					event.preventDefault();
					stopPropagation(event);

					args.grid
						.getEditorLock()
						.cancelCurrentEdit();

					restoreGridFocus();
					return;
				}

				/*
				* Arrow keys commit the current value,
				* close the editor, and navigate
				* to the adjacent cell.
				*/
				if (
					isArrowLeft ||
					isArrowRight ||
					isArrowUp ||
					isArrowDown
				) {
					event.preventDefault();
					stopPropagation(event);

					if (isArrowLeft) {
						commitAndNavigate(
							"navigateLeft"
						);
					} else if (isArrowRight) {
						commitAndNavigate(
							"navigateRight"
						);
					} else if (isArrowUp) {
						commitAndNavigate(
							"navigateUp"
						);
					} else {
						commitAndNavigate(
							"navigateDown"
						);
					}
				}
			}
		);

		/*
		 * Return a cleanup function that removes
		 * the editor-specific keyboard handler.
		 */
		return function () {
			$input.off(
				".accessibleSlickGridEditor"
			);
		};
	}
	function disableInternalTabStops(
		$container
	) {
		let viewportLabelledBy =
			$container.attr(
				"aria-labelledby"
			);

		let viewportLabel =
			$container.attr(
				"aria-label"
			);

		$container
			.find(".slick-viewport")
			.attr({
				tabindex: "0",
				role: "grid"
			});

		if (viewportLabelledBy) {
			$container
				.find(".slick-viewport")
				.attr(
					"aria-labelledby",
					viewportLabelledBy
				);
		} else if (viewportLabel) {
			$container
				.find(".slick-viewport")
				.attr(
					"aria-label",
					viewportLabel
				);
		}

		$container
			.find(
				".grid-canvas," +
				" .slick-focus-sink"
			)
			.attr(
				"tabindex",
				"-1"
			);

		/*
		 * SlickGrid always binds its click handler to .grid-canvas
		 * (slick.grid.js init(), unconditional on editable/readOnly).
		 * A populated canvas is the row group for the viewport grid.
		 * The remote model renders its empty-result message as a plain
		 * child div, so in that state the canvas becomes the row and the
		 * message becomes its gridcell.
		 */
		$container
			.find(".grid-canvas")
			.each(function () {
				let $canvas = $(this);
				let $rows =
					$canvas.children(
						".slick-row"
					);
				let $emptyContent =
					$canvas.children()
						.not(".slick-row");

				if (
					!$rows.length &&
					$emptyContent.length
				) {
					$canvas.attr(
						"role",
						"row"
					);
					$emptyContent.attr(
						"role",
						"gridcell"
					);
				} else {
					$canvas.attr(
						"role",
						"rowgroup"
					);
				}
			});

		/*
		 * Older SlickGrid versions create hidden focus sinks as direct
		 * child div elements with a tabindex attribute.
		 */
		$container
			.children("div[tabindex]")
			.not(".slick-viewport")
			.not(".grid-canvas")
			.attr(
				"tabindex",
				"-1"
			);

		/*
		 * Buttons and links inside cells are not separate Tab stops.
		 */
		$container
			.find("[data-grid-action]")
			.attr(
				"tabindex",
				"-1"
			);
	}

	function syncActiveDescendant(
		$container,
		grid
	) {
		/*
		 * A header cell is virtually focused. A re-render (e.g. after
		 * an async remote data reload triggered by sorting) can make
		 * SlickGrid re-apply its own native "active" class to the data
		 * cell matching setActiveCell's bookkeeping, so strip any such
		 * stray classes here, but otherwise leave the header's focus
		 * and aria-activedescendant alone.
		 */
		if (
			$container
				.find(
					".slick-header-column" +
					".accessible-active-header"
				)
				.length
		) {
			$container
				.find(
					".slick-cell.accessible-active-cell," +
					" .slick-cell.active"
				)
				.removeClass(
					"accessible-active-cell active"
				);

			return;
		}

		let gridFocusTarget =
			getGridFocusTarget(
				$container
			);

		$container
			.find(
				".slick-cell.accessible-active-cell"
			)
			.removeClass(
				"accessible-active-cell"
			);

		let activeCell =
			grid.getActiveCell();

		if (!activeCell) {
			$(gridFocusTarget).removeAttr(
				"aria-activedescendant"
			);

			return;
		}

		let cellNode =
			grid.getCellNode(
				activeCell.row,
				activeCell.cell
			);

		if (!cellNode) {
			$(gridFocusTarget).removeAttr(
				"aria-activedescendant"
			);

			return;
		}

		if (!cellNode.id) {
			cellNode.id =
				$container.attr("id") +
				"-cell-" +
				activeCell.row +
				"-" +
				activeCell.cell;
		}

		$(cellNode).addClass(
			"accessible-active-cell"
		);

		$(gridFocusTarget).attr(
			"aria-activedescendant",
			cellNode.id
		);
		let activeElement = document.activeElement;

		let focusIsInside =
			activeElement === gridFocusTarget ||
			$.contains(gridFocusTarget, activeElement);

		$container.toggleClass(
			"accessible-grid-focused",
			focusIsInside
		);
	}

	function applyRoles(
		$container,
		grid,
		options
	) {
		let containerId =
			$container.attr("id");

		$container.removeAttr(
			"role"
		);
		$container.removeAttr(
			"tabindex"
		);
		$container.removeAttr(
			"aria-activedescendant"
		);

		disableInternalTabStops(
			$container
		);

		let $headerRow =
			$container
				.find(".slick-header-columns")
				.first();

		if ($headerRow.length) {
			if (!$headerRow.attr("id")) {
				$headerRow.attr(
					"id",
					containerId + "-header-row"
				);
			}

			$headerRow.attr(
				"role",
				"row"
			);
		}

		let $headerCells =
			$container.find(
				".slick-header-columns" +
				" > .slick-header-column"
			);

		$headerCells.each(
			function (index) {
				if (!this.id) {
					this.id =
						containerId +
						"-header-" +
						index;
				}

				this.setAttribute(
					"role",
					"columnheader"
				);

				let $headerCell =
					$(this);

				if (
					options &&
					options.hideEmpty === true
				) {
					$headerCell
						.find(
							".slick-sort-indicator," +
							" .slick-resizable-handle"
						)
						.attr(
							"aria-hidden",
							"true"
						);
				}

			/*
				 * SlickGrid only appends .slick-sort-indicator
				 * when the column's own "sortable" option is
				 * true, so its presence reliably marks a
				 * sortable column without touching Slick core.
				 */
				let isSortable =
					$headerCell
						.find(
							".slick-sort-indicator"
						)
						.length > 0;

				if (!isSortable) {
					this.removeAttribute(
						"aria-sort"
					);

					return;
				}

				let isSorted =
					$headerCell.hasClass(
						"slick-header-column-sorted"
					);

				if (!isSorted) {
					this.setAttribute(
						"aria-sort",
						"none"
					);

					return;
				}

				let isDescending =
					$headerCell
						.find(
							".slick-sort-indicator-desc"
						)
						.length > 0;

				this.setAttribute(
					"aria-sort",
					isDescending
						? "descending"
						: "ascending"
				);
			}
		);

		let rowEntries = [];

		$container
			.find(".slick-row")
			.each(function (domIndex) {
				let $row =
					$(this);

				$row
					.attr(
						"role",
						"row"
					)
					.attr(
						"aria-label",
						""
					);

				let rowIndex =
					parseInt(
						$row.attr("row"),
						10
					);

				let sortIndex =
					isNaN(rowIndex)
						? domIndex
						: rowIndex;

				if (!this.id) {
					this.id =
						containerId +
						"-row-" +
						sortIndex;
				}

				rowEntries.push({
					id: this.id,
					sortIndex: sortIndex
				});
			});

		/*
		 * .slick-row elements are appended to the DOM in
		 * whatever order SlickGrid rendered them (virtualized,
		 * absolutely positioned), not necessarily top-to-bottom.
		 * Sort by row index so aria-owns lists them in visual order.
		 */
		rowEntries.sort(function (a, b) {
			return a.sortIndex - b.sortIndex;
		});

		if ($headerRow.length && $headerRow.attr("id")) {
			/*
			 * The header row lives outside the grid's real DOM
			 * subtree (a sibling of the viewport), so it is only
			 * reachable via aria-owns. Elements referenced by
			 * aria-owns are placed after natural DOM children in
			 * the accessibility tree, so the data rows (natural
			 * children via .grid-canvas) must be listed here too,
			 * in order, or the header gets announced last.
			 */
			let ownedIds =
				[$headerRow.attr("id")].concat(
					rowEntries.map(function (entry) {
						return entry.id;
					})
				);

			$(getGridFocusTarget(
				$container
			)).attr(
				"aria-owns",
				ownedIds.join(" ")
			);
		}

		$container
			.find(".slick-cell")
			.each(function () {

				let columnIndex =
					getColumnIndexFromCell(
						this
					);

				let rowIndex =
					getRowIndexFromCell(
						this
					);

				let columns =
					grid.getColumns();

				let column =
					columns[columnIndex];

				let dataItem =
					typeof grid.getDataItem ===
						"function"
						? grid.getDataItem(
							rowIndex
						)
						: null;

				/*
				 * The viewport is always role="grid" (single
				 * tab stop, arrow-key navigation, sortable headers)
				 * regardless of readOnly, so cells must always be
				 * role="gridcell" — role="cell" is only valid under
				 * role="table" and is not honored under role="grid".
				 */
				this.setAttribute(
					"role",
					"gridcell"
				);

				if (
					!this.id &&
					rowIndex >= 0 &&
					columnIndex >= 0
				) {
					this.id =
						containerId +
						"-cell-" +
						rowIndex +
						"-" +
						columnIndex;
				}

				/*
				 * Remove any previously assigned
				 * accessible name because SlickGrid
				 * reuses cell elements.
				 */
				this.removeAttribute(
					"aria-label"
				);

				if (
					!column ||
					!dataItem
				) {
					return;
				}

				let email =
					dataItem.EmailAddress ||
					"";

				let cellText =
					$.trim(
						$(this).text()
					);

				/*
				 * Name column:
				 * when the visible name is missing,
				 * expose the email instead.
				 */
				if (
					column.id === "Name" &&
					!cellText
				) {
					this.setAttribute(
						"aria-label",
						email
							? "Name not provided for " +
							email
							: "Name not provided"
					);
				}

				/*
				 * Confirmed column:
				 * the cell contains only an icon
				 * or is visually empty.
				 */
				if (
					column.id ===
					"Confirmed"
				) {
					this.setAttribute(
						"aria-label",
						email +
						(
							dataItem.Confirmed
								? " confirmed"
								: " not confirmed"
						)
					);
				}
			});

		syncActiveDescendant(
			$container,
			grid
		);
	}
	function ensureActiveCell(
		$container,
		grid
	) {
		let activeCell =
			grid.getActiveCell();

		if (activeCell) {
			let existingCellNode =
				grid.getCellNode(
					activeCell.row,
					activeCell.cell
				);

			if (existingCellNode) {
				syncActiveDescendant(
					$container,
					grid
				);

				return activeCell;
			}
		}

		let selectedCell = null;

		$container
			.find(
				".slick-row .slick-cell"
			)
			.each(function () {
				let rowIndex =
					getRowIndexFromCell(
						this
					);

				let columnIndex =
					getColumnIndexFromCell(
						this
					);

				if (
					rowIndex < 0 ||
					columnIndex < 0
				) {
					return;
				}

				if (
					typeof grid
						.canCellBeActive ===
					"function" &&
					!grid.canCellBeActive(
						rowIndex,
						columnIndex
					)
				) {
					return;
				}

				selectedCell = {
					row: rowIndex,
					cell: columnIndex
				};

				return false;
			});

		if (!selectedCell) {
			$(getGridFocusTarget(
				$container
			)).removeAttr(
				"aria-activedescendant"
			);

			return null;
		}

		grid.setActiveCell(
			selectedCell.row,
			selectedCell.cell
		);

		/*
		 * setActiveCell may move focus to an internal SlickGrid element.
		 * Return focus to the grid root.
		 */
		focusContainer(
			getGridFocusTarget(
				$container
			)
		);

		syncActiveDescendant(
			$container,
			grid
		);

		return selectedCell;
	}
	function wrapHorizontalNavigation(
		grid,
		previousCell,
		methodName
	) {
		let columns =
			grid.getColumns();

		let rowCount =
			grid.getDataLength();

		if (
			methodName === "navigateRight" &&
			previousCell.cell === columns.length - 1 &&
			previousCell.row < rowCount - 1
		) {
			grid.setActiveCell(
				previousCell.row + 1,
				0
			);

			return;
		}

		if (
			methodName === "navigateLeft" &&
			previousCell.cell === 0 &&
			previousCell.row > 0
		) {
			grid.setActiveCell(
				previousCell.row - 1,
				columns.length - 1
			);
		}
	}

	/*
	 * Home/End: move to the first/last cell of the current row that
	 * can become active (e.g. past a non-selectable spacer column).
	 * Ctrl+Home/End (toGridEdge): the same, but in the first/last
	 * row of the grid. Slick core has no navigation method for
	 * this, so the active cell is set directly and focus/ARIA are
	 * synced the same way navigate() does afterwards.
	 */
	function navigateToRowEdge(
		$container,
		grid,
		toEnd,
		toGridEdge
	) {
		let activeCell =
			ensureActiveCell(
				$container,
				grid
			);

		if (!activeCell) {
			return;
		}

		let columns =
			grid.getColumns();

		let row = activeCell.row;

		if (toGridEdge) {
			row = toEnd
				? Math.max(
					grid.getDataLength() - 1,
					0
				)
				: 0;
		}

		let step = toEnd ? -1 : 1;
		let cell = toEnd
			? columns.length - 1
			: 0;

		while (
			(
				row !== activeCell.row ||
				cell !== activeCell.cell
			) &&
			cell >= 0 &&
			cell < columns.length &&
			typeof grid.canCellBeActive ===
			"function" &&
			!grid.canCellBeActive(
				row,
				cell
			)
		) {
			cell += step;
		}

		/*
		 * Only possible when the target row differs from the
		 * active one and none of its cells can become active.
		 */
		if (
			cell < 0 ||
			cell >= columns.length
		) {
			return;
		}

		grid.setActiveCell(
			row,
			cell
		);

		focusContainer(
			getGridFocusTarget(
				$container
			)
		);

		syncActiveDescendant(
			$container,
			grid
		);
	}

	function navigate(
		$container,
		grid,
		methodName
	) {
		let previousCell =
			ensureActiveCell(
				$container,
				grid
			);

		if (!previousCell) {
			return;
		}

		if (
			typeof grid[methodName] ===
			"function"
		) {
			grid[methodName]();
		}

		let currentCell =
			grid.getActiveCell();

		/*
		 * SlickGrid does not wrap horizontal navigation
		 * between rows in this version.
		 */
		if (
			currentCell &&
			currentCell.row === previousCell.row &&
			currentCell.cell === previousCell.cell
		) {
			wrapHorizontalNavigation(
				grid,
				previousCell,
				methodName
			);
		}

		/*
		 * grid[methodName]() above (Slick core) moves real DOM focus
		 * to SlickGrid's own canvas element synchronously. Correct it
		 * back to the grid root in this same tick, before returning
		 * control to the browser, so it never gets a chance to notify
		 * the AT of that transient, unnamed focus target — deferring
		 * this correction to the setTimeout below left a window where
		 * NVDA announced the momentary focus on .grid-canvas as "blank"
		 * on every arrow-key move. The setTimeout still runs afterward
		 * for the editor-focus-transfer case, which needs the cell to
		 * have finished rendering.
		 */
		focusContainer(
			getGridFocusTarget(
				$container
			)
		);

		syncActiveDescendant(
			$container,
			grid
		);

		window.setTimeout(function () {
			let activeCell =
				grid.getActiveCell();

			let editorElement =
				null;

			if (activeCell) {
				let cellNode =
					grid.getCellNode(
						activeCell.row,
						activeCell.cell
					);

				if (cellNode) {
					editorElement =
						cellNode.querySelector(
							"select.griddrop"
						);
				}
			}

			if (editorElement) {
				try {
					editorElement.focus({
						preventScroll: true
					});
				} catch (error) {
					editorElement.focus();
				}
			} else {
				focusContainer(
					getGridFocusTarget(
						$container
					)
				);
			}

			syncActiveDescendant(
				$container,
				grid
			);
		}, 0);
	}

	function bindKeyboardHandling(
		$container,
		grid,
		options
	) {
		let container =
			$container[0];
		let $gridFocusTarget =
			$(
				getGridFocusTarget(
					$container
				)
			);
		let headerFocusIndex = -1;
		let lastHeaderFocusIndex = 0;
		let bodyEnteredFromHeader = false;
		let pendingHeaderRestoreIndex = -1;
		let restoringHeaderFocus = false;

		function focusFirstDataCell() {
			let selectedCell = null;

			$container
				.find(
					".slick-row .slick-cell"
				)
				.each(function () {
					let rowIndex =
						getRowIndexFromCell(
							this
						);

					let columnIndex =
						getColumnIndexFromCell(
							this
						);

					if (
						rowIndex < 0 ||
						columnIndex < 0
					) {
						return;
					}

					if (
						typeof grid
							.canCellBeActive ===
							"function" &&
						!grid.canCellBeActive(
							rowIndex,
							columnIndex
						)
					) {
						return;
					}

					selectedCell = {
						row: rowIndex,
						cell: columnIndex
					};

					return false;
				});

			if (!selectedCell) {
				return false;
			}

			grid.setActiveCell(
				selectedCell.row,
				selectedCell.cell
			);

			focusContainer(
				getGridFocusTarget(
					$container
				)
			);

			syncActiveDescendant(
				$container,
				grid
			);

			return true;
		}

		function handleFocus(event) {
			let gridFocusTarget =
				getGridFocusTarget(
					$container
				);

			if (
				event.target !== gridFocusTarget
			) {
				return;
			}

			/*
			 * focusHeaderCell() moves real DOM focus twice - SlickGrid's
			 * setActiveCell() throws it onto .grid-canvas and the call after it
			 * pulls it back onto the viewport - and the second move lands right
			 * back here. Without this guard that is an endless loop that ends in
			 * "Maximum call stack size exceeded"; the loop used to break itself
			 * only because the entry condition stopped holding after the first
			 * setActiveCell(), which is why the header could be reached exactly
			 * once per page load.
			 */
			if (restoringHeaderFocus) {
				return;
			}

			if (headerFocusIndex === -1) {
				let forwardTab =
					options &&
						options.tabFromHeaderToBody === true
						? tabDirectionIntoGrid()
						: null;

				let restoreIndex;

				if (forwardTab === true) {
					/*
					 * Tabbed in from the control before the grid: the header
					 * row is the first stop every time, not just on the first
					 * visit. Resume on the column last worked with.
					 */
					restoreIndex =
						pendingHeaderRestoreIndex !== -1
							? pendingHeaderRestoreIndex
							: lastHeaderFocusIndex;
				} else if (forwardTab === false) {
					/*
					 * Shift+Tabbed in from the control after the grid: the body
					 * comes first, and the next Shift+Tab steps up to the header.
					 */
					restoreIndex = -1;
				} else {
					restoreIndex =
						pendingHeaderRestoreIndex !== -1
							? pendingHeaderRestoreIndex
							: !grid.getActiveCell()
								? 0
								: -1;
				}

				pendingHeaderRestoreIndex = -1;

				if (restoreIndex !== -1) {
					restoringHeaderFocus = true;

					try {
						headerFocusIndex =
							focusHeaderCell(
								$container,
								grid,
								restoreIndex
							);
					} finally {
						restoringHeaderFocus = false;
					}

					if (headerFocusIndex !== -1) {
						return;
					}
				}
			}

			ensureActiveCell(
				$container,
				grid
			);
		}

		function handleFocusOut() {
			window.setTimeout(function () {
				let activeElement =
					document.activeElement;

				if (
					activeElement &&
					container.contains(activeElement)
				) {
					return;
				}

				clearVisualFocus($container);

				if (headerFocusIndex !== -1) {
					pendingHeaderRestoreIndex =
						headerFocusIndex;
				}

				headerFocusIndex = -1;
				bodyEnteredFromHeader = false;

				$(getGridFocusTarget(
					$container
				)).removeAttr(
					"aria-activedescendant"
				);
			}, 0);
		}

		function focusAdjacentElement(
			$container,
			moveBackward
		) {
			let container =
				$container[0];
			let gridFocusTarget =
				getGridFocusTarget(
					$container
				);

			let selector =
				'a[href],' +
				'button:not([disabled]),' +
				'input:not([disabled]):not([type="hidden"]),' +
				'select:not([disabled]),' +
				'textarea:not([disabled]),' +
				'[tabindex]:not([tabindex="-1"])';

			let elements =
				Array.prototype.filter.call(
					document.querySelectorAll(
						selector
					),
					function (element) {
						return !!(
							element.offsetWidth ||
							element.offsetHeight ||
							element.getClientRects().length
						);
					}
				);

			let containerIndex =
				elements.indexOf(
					gridFocusTarget
				);

			if (containerIndex < 0) {
				return;
			}

			let step =
				moveBackward
					? -1
					: 1;

			let index =
				containerIndex + step;

			while (
				index >= 0 &&
				index < elements.length
			) {
				let element =
					elements[index];

				if (
					element !== gridFocusTarget &&
					!container.contains(element)
				) {
					focusContainer(element);
					return;
				}

				index += step;
			}
		}
		function focusActiveEditor(
			row,
			cell,
			attempt
		) {
			let cellNode =
				grid.getCellNode(
					row,
					cell
				);

			let editorElement =
				cellNode
					? cellNode.querySelector(
						"select.griddrop," +
						" input," +
						" textarea"
					)
					: null;

			if (editorElement) {
				focusContainer(
					editorElement
				);

				syncActiveDescendant(
					$container,
					grid
				);

				return;
			}

			/*
			 * SlickGrid may create the editor asynchronously.
			 * Retry for several animation frames.
			 */
			if (attempt < 5) {
				window.requestAnimationFrame(
					function () {
						focusActiveEditor(
							row,
							cell,
							attempt + 1
						);
					}
				);

				return;
			}

			focusContainer(
				getGridFocusTarget(
					$container
				)
			);

			syncActiveDescendant(
				$container,
				grid
			);
		}

		function activateCellEditor(
			activeCell
		) {
			let editorLock =
				grid.getEditorLock();

			if (
				editorLock &&
				editorLock.isActive &&
				editorLock.isActive()
			) {
				if (
					!editorLock.commitCurrentEdit()
				) {
					return;
				}
			}

			grid.setActiveCell(
				activeCell.row,
				activeCell.cell
			);

			if (
				typeof grid.editActiveCell ===
				"function"
			) {
				grid.editActiveCell();
			}

			let cellNode =
				grid.getCellNode(
					activeCell.row,
					activeCell.cell
				);

			let editorElement =
				cellNode
					? cellNode.querySelector(
						"select.griddrop," +
						" select," +
						" input," +
						" textarea"
					)
					: null;

			if (editorElement) {
				focusContainer(
					editorElement
				);

				/*
				 * Open the native select picker from
				 * the current trusted keyboard event.
				 */
				if (
					editorElement.tagName ===
					"SELECT" &&
					typeof editorElement.showPicker ===
					"function"
				) {
					try {
						editorElement.showPicker();
					} catch (error) {
						/*
						 * The browser may block programmatic
						 * opening, but the editor remains focused.
						 */
					}
				}

				syncActiveDescendant(
					$container,
					grid
				);

				return;
			}

			/*
			 * Some SlickGrid versions render the editor
			 * on the next frame.
			 */
			window.requestAnimationFrame(
				function () {
					let renderedCellNode =
						grid.getCellNode(
							activeCell.row,
							activeCell.cell
						);

					let renderedEditor =
						renderedCellNode
							? renderedCellNode.querySelector(
								"select.griddrop," +
								" select," +
								" input," +
								" textarea"
							)
							: null;

					if (renderedEditor) {
						focusContainer(
							renderedEditor
						);
					}

					syncActiveDescendant(
						$container,
						grid
					);
				}
			);
		}

		function handleKeyDownCapture(event) {
			let $target =
				$(event.target);

			/*
			 * Tab must be handled before native controls are excluded.
			 * Otherwise a select editor passes Tab to SlickGrid navigation.
			 */
			if (isTabKey(event)) {
				consumeEvent(event);

				let editorLock =
					grid.getEditorLock();

				if (
					editorLock &&
					!editorLock.commitCurrentEdit()
				) {
					return;
				}

				let moveBackward =
					event.shiftKey;

				if (
					options &&
					options.tabFromHeaderToBody === true &&
					!moveBackward &&
					headerFocusIndex !== -1
				) {
					lastHeaderFocusIndex =
						headerFocusIndex;
					headerFocusIndex = -1;

					$container
						.find(
							".slick-header-column" +
							".accessible-active-header"
						)
						.removeClass(
							"accessible-active-header"
						);

					if (focusFirstDataCell()) {
						bodyEnteredFromHeader = true;
						return;
					}
				}

				/*
				 * The header row is a tab stop of its own, so Shift+Tab out of
				 * the body always lands on it - not only when the body was
				 * entered from the header. Reaching the body from the control
				 * after the grid (Shift+Tab into it) used to skip the header on
				 * the way back out. lastHeaderFocusIndex starts at 0, so a body
				 * that was never left through the header returns to the first
				 * column. If the grid has no header cells at all there is
				 * nothing to stop at and focus leaves the grid as before.
				 */
				if (
					options &&
					options.tabFromHeaderToBody === true &&
					moveBackward &&
					headerFocusIndex === -1
				) {
					let restoredHeaderIndex =
						focusHeaderCell(
							$container,
							grid,
							lastHeaderFocusIndex
						);

					if (restoredHeaderIndex !== -1) {
						headerFocusIndex =
							restoredHeaderIndex;

						bodyEnteredFromHeader = false;
						return;
					}
				}

				bodyEnteredFromHeader = false;

				window.setTimeout(function () {
					clearVisualFocus(
						$container
					);

					if (headerFocusIndex !== -1) {
						pendingHeaderRestoreIndex =
							headerFocusIndex;
					}

					headerFocusIndex = -1;

					$container.removeAttr(
						"aria-activedescendant"
					);

					focusAdjacentElement(
						$container,
						moveBackward
					);
				}, 0);

				return;
			}

			/*
			 * Native controls own all keyboard input other than Tab.
			 * Select arrow handling is implemented separately
			 * in bindSelectEditorKeyboard().
			 */
			if (
				$target.is(
					"input, select, textarea," +
					" button, a," +
					'[contenteditable="true"]'
				)
			) {
				return;
			}

			let key =
				event.key ||
				event.keyCode;

			let isHome =
				key === "Home" ||
				key === 36;

			let isEnd =
				key === "End" ||
				key === 35;

			/*
			 * Ctrl+Home/End (first/last cell of the grid) is the
			 * one modified combination handled here; everything
			 * else with a modifier is left to the browser.
			 */
			let isCtrlHomeEnd =
				(isHome || isEnd) &&
				event.ctrlKey &&
				!event.altKey &&
				!event.metaKey;

			if (
				!isPlainKey(event) &&
				!isCtrlHomeEnd
			) {
				return;
			}

			let isEnter =
				key === "Enter" ||
				key === 13;

			let isSpace =
				key === " " ||
				key === "Spacebar" ||
				key === 32;

			let isEscape =
				key === "Escape" ||
				key === "Esc" ||
				key === 27;

			let isArrowLeft =
				key === "ArrowLeft" ||
				key === "Left" ||
				key === 37;

			let isArrowUp =
				key === "ArrowUp" ||
				key === "Up" ||
				key === 38;

			let isArrowRight =
				key === "ArrowRight" ||
				key === "Right" ||
				key === 39;

			let isArrowDown =
				key === "ArrowDown" ||
				key === "Down" ||
				key === 40;

			if (isHome || isEnd) {
				consumeEvent(event);

				if (headerFocusIndex !== -1) {
					headerFocusIndex =
						focusHeaderCell(
							$container,
							grid,
							isHome
								? 0
								: getHeaderCells(
									$container
								).length - 1
						);

					return;
				}

				navigateToRowEdge(
					$container,
					grid,
					isEnd,
					isCtrlHomeEnd
				);

				return;
			}

			if (isEscape) {
				if (headerFocusIndex !== -1) {
					consumeEvent(event);

					headerFocusIndex = -1;

					$container
						.find(
							".slick-header-column" +
							".accessible-active-header"
						)
						.removeClass(
							"accessible-active-header"
						);

					focusContainer(
						getGridFocusTarget(
							$container
						)
					);

					ensureActiveCell(
						$container,
						grid
					);

					return;
				}

				let escapeEditorLock =
					grid.getEditorLock();

				if (escapeEditorLock) {
					escapeEditorLock
						.cancelCurrentEdit();
				}

				consumeEvent(event);

				window.setTimeout(function () {
					focusContainer(
						getGridFocusTarget(
							$container
						)
					);

					syncActiveDescendant(
						$container,
						grid
					);
				}, 0);

				return;
			}

			if (isEnter || isSpace) {
				if (headerFocusIndex !== -1) {
					consumeEvent(event);

					let headerCells =
						getHeaderCells(
							$container
						);

					let headerCell =
						headerCells[
							headerFocusIndex
						];

					let headerColumnIndex =
						headerFocusIndex;

					if (headerCell) {
						headerCell.click();
					}

					/*
					 * Sorting re-renders asynchronously and calls
					 * refresh(), which moves virtual focus to a data
					 * cell. Re-claim the header afterward so the user
					 * stays on it to keep sorting or move left/right.
					 */
					window.setTimeout(function () {
						headerFocusIndex =
							focusHeaderCell(
								$container,
								grid,
								headerColumnIndex
							);
					}, 0);

					return;
				}

				let activeCell =
					grid.getActiveCell();

				if (!activeCell) {
					activeCell =
						ensureActiveCell(
							$container,
							grid
						);
				}

				if (!activeCell) {
					return;
				}

				let cellNode =
					grid.getCellNode(
						activeCell.row,
						activeCell.cell
					);

				if (!cellNode) {
					return;
				}

				let removeButton =
					cellNode.querySelector(
						'[data-grid-action="remove-user"],' +
						'[data-grid-action="delete-user"]'
					);

				if (removeButton) {
					consumeEvent(event);
					removeButton.click();
					return;
				}

				let columns =
					grid.getColumns();

				let column =
					columns[activeCell.cell];

				if (
					column &&
					column.editor
				) {
					consumeEvent(event);

					activateCellEditor(
						activeCell
					);

					return;
				}

				let actionButton =
					cellNode.querySelector(
						"[data-grid-action]"
					);

				if (actionButton) {
					consumeEvent(event);
					actionButton.click();
				}

				return;
			}

			if (isArrowLeft) {
				consumeEvent(event);

				if (headerFocusIndex !== -1) {
					headerFocusIndex =
						focusHeaderCell(
							$container,
							grid,
							headerFocusIndex - 1
						);

					return;
				}

				navigate(
					$container,
					grid,
					"navigateLeft"
				);

				return;
			}

			if (isArrowUp) {
				consumeEvent(event);

				if (headerFocusIndex !== -1) {
					return;
				}

				let activeCellForHeader =
					ensureActiveCell(
						$container,
						grid
					);

				if (
					activeCellForHeader &&
					activeCellForHeader.row === 0
				) {
					headerFocusIndex =
						focusHeaderCell(
							$container,
							grid,
							activeCellForHeader.cell
						);
					lastHeaderFocusIndex =
						headerFocusIndex;

					return;
				}

				navigate(
					$container,
					grid,
					"navigateUp"
				);

				return;
			}

			if (isArrowRight) {
				consumeEvent(event);

				if (headerFocusIndex !== -1) {
					headerFocusIndex =
						focusHeaderCell(
							$container,
							grid,
							headerFocusIndex + 1
						);

					return;
				}

				navigate(
					$container,
					grid,
					"navigateRight"
				);

				return;
			}

			if (isArrowDown) {
				consumeEvent(event);

				if (headerFocusIndex !== -1) {
					let headerColumnIndex =
						headerFocusIndex;

					headerFocusIndex = -1;

					$container
						.find(
							".slick-header-column" +
							".accessible-active-header"
						)
						.removeClass(
							"accessible-active-header"
						);

					grid.setActiveCell(
						0,
						headerColumnIndex
					);

					focusContainer(
						getGridFocusTarget(
							$container
						)
					);

					syncActiveDescendant(
						$container,
						grid
					);

					return;
				}

				navigate(
					$container,
					grid,
					"navigateDown"
				);
			}
		}

		if (
			container
				.__accessibleSlickGridKeyHandler
		) {
			container.removeEventListener(
				"keydown",
				container
					.__accessibleSlickGridKeyHandler,
				true
			);
		}

		container
			.__accessibleSlickGridKeyHandler =
			handleKeyDownCapture;

		container.addEventListener(
			"keydown",
			handleKeyDownCapture,
			true
		);

		$container
			.off(
				".accessibleSlickGrid"
			)
			.on(
				"focus.accessibleSlickGrid",
				handleFocus
			).on(
				"focusout.accessibleSlickGrid",
				handleFocusOut
			);

		$gridFocusTarget
			.off(
				".accessibleSlickGridViewport"
			)
			.on(
				"focus.accessibleSlickGridViewport",
				handleFocus
			)
			.on(
				"focusout.accessibleSlickGridViewport",
				handleFocusOut
			);
	}

	function clearVisualFocus($container) {
		$container
			.find(".slick-cell.accessible-active-cell")
			.removeClass("accessible-active-cell");

		$container
			.find(".slick-header-column.accessible-active-header")
			.removeClass("accessible-active-header");

		$container.removeClass(
			"accessible-grid-focused"
		);
	}

	function bindGridEvents(
		$container,
		grid,
		options
	) {
		if (
			$container.data(
				"accessibleSlickGridBoundGrid"
			) === grid
		) {
			return;
		}

		$container.data(
			"accessibleSlickGridBoundGrid",
			grid
		);

		if (
			grid.onActiveCellChanged
		) {
			grid.onActiveCellChanged
				.subscribe(function () {
					syncActiveDescendant(
						$container,
						grid
					);
				});
		}

		/*
		 * SlickGrid virtualizes rows: scrolling (and resizing/data
		 * reload) discards off-screen row/cell DOM nodes and creates
		 * fresh ones for rows entering the viewport. Those new nodes
		 * never receive the role/id applyRoles sets,
		 * so re-apply on every viewport change to cover them.
		 */
		if (
			grid.onViewportChanged
		) {
			grid.onViewportChanged
				.subscribe(function () {
					window.setTimeout(function () {
						applyRoles(
							$container,
							grid,
							options
						);
					}, 0);
				});
		}

		/*
		 * A large SlickGrid scroll can defer render() for 50ms but fire
		 * onViewportChanged immediately. Observe the canvas so roles are
		 * applied after the replacement rows actually enter the DOM.
		 */
		if (
			window.MutationObserver
		) {
			let canvas =
				$container
					.find(".grid-canvas")
					.first()[0];

			if (canvas) {
				let refreshPending = false;
				let observer =
					new window.MutationObserver(
						function () {
							if (refreshPending) {
								return;
							}

							refreshPending = true;
							window.setTimeout(function () {
								refreshPending = false;

								if (
									$.contains(
										document.documentElement,
										$container[0]
									)
								) {
									applyRoles(
										$container,
										grid,
										options
									);
								}
							}, 0);
						}
					);

				observer.observe(
					canvas,
					{
						childList: true,
						subtree: true
					}
				);

				$container.data(
					"accessibleSlickGridMutationObserver",
					observer
				);

				if (grid.onBeforeDestroy) {
					grid.onBeforeDestroy
						.subscribe(function () {
							observer.disconnect();
							$container.removeData(
								"accessibleSlickGridMutationObserver"
							);
						});
				}
			}
		}
	}

	function enhance(
		container,
		grid,
		options
	) {
		let $container =
			container &&
				container.jquery
				? container
				: $(container);

		if (
			!options ||
			options.enabled !== true ||
			!isOptedIn($container) ||
			!grid
		) {
			return;
		}

		applyRoles(
			$container,
			grid,
			options
		);

		bindKeyboardHandling(
			$container,
			grid,
			options
		);

		bindGridEvents(
			$container,
			grid,
			options
		);
	}

	function refresh(
		container,
		grid,
		options
	) {
		let $container =
			container &&
				container.jquery
				? container
				: $(container);

		if (
			!$container.length ||
			!grid ||
			!isOptedIn($container)
		) {
			return;
		}

		applyRoles(
			$container,
			grid,
			options
		);

		if (
			document.activeElement ===
			getGridFocusTarget(
				$container
			)
		) {
			ensureActiveCell(
				$container,
				grid
			);
		}
	}

	return {
		enhance: enhance,
		refresh: refresh,
		escapeHtml: escapeHtml,
		editRoleAriaLabel:
			editRoleAriaLabel,
		removeUserAriaLabel:
			removeUserAriaLabel,
		buildEditButtonHtml:
			buildEditButtonHtml,

		bindSelectEditorKeyboard:
			bindSelectEditorKeyboard
	};
}
